import React from 'react';
import { Box, Button, Chip, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { convertFromRaw, convertToRaw, EditorState, Modifier } from 'draft-js';
import { useDeepEffect } from '../../../../../../hooks/useDeepEffect';

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      '& .buttonsGroup': {
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
      },
      '& .button': {
        borderRadius: 6,
        height: 37,
        textTransform: 'none',
      },
    },
    mergeTagsContainer: {
      marginBottom: '8px',
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
      marginTop: 4,
    },
    subtitle: {
      fontSize: 12,
      color: '#9E9E9E',
    },
    cta: {
      color: '#7047EB',
      cursor: 'pointer',
      fontSize: 12,
      paddingTop: 2,
      fontWeight: 400,
    },
  })
);

interface FullBodyEditorProps {
  value?: string;
  onSave: (content: string) => void;
  availableMergeTags: string[];
}

function createEditorState(value?: string): EditorState {
  if (!value) return EditorState.createEmpty();
  try {
    return EditorState.createWithContent(convertFromRaw(JSON.parse(value)));
  } catch {
    return EditorState.createEmpty();
  }
}

export function FullBodyEditor({ value, onSave, availableMergeTags }: FullBodyEditorProps) {
  const [editingMode, setEditingMode] = React.useState(!value);
  const [editorState, setEditorState] = React.useState(() => createEditorState(value));
  const classes = useStyles();

  useDeepEffect(() => {
    setEditorState(createEditorState(value));
  }, [value]);

  const onSaveHandler = () => {
    onSave(JSON.stringify(convertToRaw(editorState.getCurrentContent())));
    setEditingMode(false);
  };

  const insertMergeTag = (tag: string) => {
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const newContent = Modifier.insertText(contentState, selection, tag);
    const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
    setEditorState(newEditorState);
  };

  if (editingMode) {
    return (
      <Box className={classes.container}>
        <Box className={classes.mergeTagsContainer}>
          <Typography className={classes.subtitle}>Available merge tags (click to insert):</Typography>
          <Box className={classes.chipContainer}>
            {availableMergeTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size='small'
                onClick={() => insertMergeTag(tag)}
                clickable
                variant='outlined'
              />
            ))}
          </Box>
        </Box>
        <CampfireRichEditor
          placeholder='Enter your custom email template content...'
          editorState={editorState}
          setEditorState={setEditorState}
        />
        <Box className='buttonsGroup'>
          <Button className='button' onClick={() => setEditingMode(false)} variant='outlined'>
            Cancel
          </Button>
          <Button className='button' variant='contained' disableElevation color='primary' onClick={onSaveHandler}>
            Save
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <CampfireRichEditor editorState={editorState} />
      <Typography className={classes.cta} onClick={() => setEditingMode(true)}>
        Click to edit
      </Typography>
    </Box>
  );
}
